import {useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DateUtils,
  IconHeaderWithSearch,
  LoadingSpinner,
  asQueryString,
  enumContainer,
  omitNullsy,
  useDefaultScrollTo,
  useLocalStorage,
  useLocationQuery,
} from '@wallaby-ui/core';
import {
  LightMonthRangeFilter,
  LightFilterPanel,
  LightSelectFilter,
  LightSelectedFiltersPane,
  TableSorting,
  UserFilterDto,
  WallabyCoreSchema,
  useFormatters,
  useOrganizationsByOrgSecurity,
  useTableData,
  useTableParams,
  useUserFilters,
  ShortcutEnum,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import * as React from 'react';
import {useCallback, useMemo} from 'react';
import {Helmet} from 'react-helmet';

import {
  DEFAULT_HOSPITAL_REPORT_SORTING,
  HaiReportMeasureType,
  HaiTypeCategory,
  hospitalReportGetStyles,
} from './HospitalReportLayoutContainer';
import {HospitalReportTableAndCharts, HospitalReportData} from './HospitalReportTableAndCharts';
import {useStandardTypes} from '../encounter/hai/useStandardTypes';
import {useAppSelector} from 'src/js/store_hooks';

type Organization = WallabyCoreSchema.components['schemas']['OrganizationFilterDto'];

const DEFAULT_REPORT_FILTERS_OPEN = true;
type HospitalNetworkReportFilters = {
  dateFrom: string;
  dateTo: string;
  floatingShortcutName: ShortcutEnum | null;
  haiState: string;
  orgLevelType: string;
  typeCategory: string;
  measureType: 'HAI_COUNT' | 'HOSP_WITH_HAI_COUNT';
};
type FiltersData = {
  reportOpen: boolean;
  report: HospitalNetworkReportFilters;
  reportSorting: TableSorting;
};
type HospitalReportLinkFilters = {
  report: Omit<HospitalNetworkReportFilters, 'orgLevelType'> & {
    organizationId?: string;
  };
};
const DEFAULT_REPORT_FILTERS = () => {
  const [startOfLastMonth, endOfLastMonth] = DateUtils.lastCompletedMonth();
  return {
    dateFrom: startOfLastMonth,
    dateTo: endOfLastMonth,
    floatingShortcutName: ShortcutEnum.PREVIOUS_MONTH,
    haiState: 'ALL',
    orgLevelType: 'HOSPITAL', // NOTE: user filter api requires orgLevelType here
    typeCategory: 'HAI_TYPE',
    measureType: 'HAI_COUNT',
  } as HospitalNetworkReportFilters;
};
const DEFAULT_REPORT_SORTING = DEFAULT_HOSPITAL_REPORT_SORTING;

function normalizeHospitalNetworkReportFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.floatingShortcutName) {
    delete copy.dateFrom;
    delete copy.dateTo;
  }
  return copy;
}

type Props = {config: ConfigObjectType};
export const HospitalNetworkReportLayoutContainer: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'hospitalGroupReport';
  const storageIdPrefix = '/haiByHospitals';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const defaultReportFilters = DEFAULT_REPORT_FILTERS();
  const filters: FiltersData = useMemo(() => {
    return {
      reportOpen: query?.reportOpen ?? DEFAULT_REPORT_FILTERS_OPEN,
      report: {
        ...defaultReportFilters,
        ...(query?.report ?? {}),
      } as HospitalNetworkReportFilters,
      reportSorting: query?.reportSorting ?? DEFAULT_REPORT_SORTING,
    };
  }, [query, defaultReportFilters]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({report: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <HospitalNetworkReportLayoutContainerInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};
HospitalNetworkReportLayoutContainer.displayName = 'HospitalNetworkReportLayoutContainer';

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Record<string, any>) => void;
};
export const HospitalNetworkReportLayoutContainerInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const pageTitle = 'navigation.route.haiByHospitals';
  const reportUrl = config.service.url.multipleDepartmentReport;
  const theme = useTheme();
  const muiStyles = hospitalReportGetStyles({theme});
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);

  const {data: organizations} = useOrganizationsByOrgSecurity<Organization>(config);
  const hospitals = organizations.filter((v) => v.levelType === 'HOSPITAL');
  const haveDefaultFilters = organizations.length > 0;
  const defaultReportFilters = DEFAULT_REPORT_FILTERS();

  const reportParams = useTableParams(
    {
      dateFrom: filters.report.dateFrom,
      dateTo: filters.report.dateTo,
      floatingShortcutName: filters.report.floatingShortcutName,
      haiState: filters.report.haiState,
      orgLevelType: 'HOSPITAL', // NOTE: orgLevelType must be "HOSPITAL" for multiple departments report
      organizationId: null, // NOTE: organizationId must be null for group level report
      typeCategory: filters.report.typeCategory,
      measureType: filters.report.measureType,
    }
    // filters.reportSorting, // NOTE: we sort only on FE
    // {}
  );
  const {data: reportData, isLoading} = useTableData<any, HospitalReportData>(reportUrl, reportParams, 'content', haveDefaultFilters);

  const formatters = useFormatters();
  const {t, tEnum, formatDate} = formatters;
  const hasFilters = !isEqual(
    normalizeHospitalNetworkReportFiltersForCompare(omitNullsy(filters.report) as any),
    normalizeHospitalNetworkReportFiltersForCompare(omitNullsy(defaultReportFilters) as any)
  );
  const hasSorting = !isEqual(omitNullsy(filters.reportSorting), omitNullsy(DEFAULT_REPORT_SORTING));
  const haiReportsStateOptions = enumMap.HaiReportsState ?? [];
  const haiTypeCategoryOptions = enumMap.HaiTypeCategory ?? HaiTypeCategory;
  const haiMeasureTypeOptions = enumMap.HaiReportMeasureType ?? HaiReportMeasureType;
  // scroll
  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.scroll`, 0);
  const scrollWrapperRef = useDefaultScrollTo(defaultScroll);
  const {standardType} = useStandardTypes({config});

  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);

  const selectedFiltersPane = (
    <LightSelectedFiltersPane
      selectedFilters={[
        {
          name: ['dateFrom', 'dateTo', 'floatingShortcutName'],
          label: t('reports.filters.date'),
          value: `${formatDate(filters.report.dateFrom)} - ${formatDate(filters.report.dateTo)}`,
          defaultValue: [defaultReportFilters.dateFrom, defaultReportFilters.dateTo, defaultReportFilters.floatingShortcutName],
        },
        {
          name: 'haiState',
          label: t('reports.filters.haiState'),
          value: tEnum('HaiReportsState', filters.report.haiState),
          defaultValue: defaultReportFilters.haiState,
        },
        {
          name: 'typeCategory',
          label: t('reports.filters.haiTypeCategory'),
          value: tEnum('HaiTypeCategory', filters.report.typeCategory),
          defaultValue: defaultReportFilters.typeCategory,
        },
        {
          name: 'measureType',
          label: t('reports.filters.haiMeasureType'),
          value: tEnum('HaiReportMeasureType', filters.report.measureType),
          defaultValue: defaultReportFilters.measureType,
        },
      ]}
      tableFilters={filters.report}
      changeTableFilters={(newTableFilters) =>
        changeFilters({
          report: newTableFilters,
        })
      }
      formatters={formatters}
    />
  );
  return (
    <div
      css={muiStyles.contentWrapper}
      ref={scrollWrapperRef}
      onScroll={(event) => {
        setDefaultScroll((event.target as HTMLElement).scrollTop);
      }}
    >
      <Helmet title={`${t('reports.list.pageTitle')} | ${t(pageTitle)}`} />
      <div css={muiStyles.splitContentBox}>
        <LightFilterPanel
          open={filters.reportOpen}
          toggleOpen={() =>
            changeFilters({
              reportOpen: !filters.reportOpen,
            })
          }
          hasActiveFilter={hasFilters}
          maxScrollAreaHeight='40vh'
          savedFiltersProps={{
            storageIdPrefix,
            component,
            userFiltersLoading,
            addedUserFilters,
            userFilters,
            changeUserFilter,
            filter: filters.report,
            setFilter: (newFilter) => {
              changeFilters({
                report: newFilter,
              });
            },
            config,
            normalizeForCompare: normalizeHospitalNetworkReportFiltersForCompare,
          }}
          selectedFilters={selectedFiltersPane}
        >
          <LightMonthRangeFilter
            config={{
              name: 'date',
              ui: {label: 'reports.filters.date'},
            }}
            value={{
              startDate: filters.report.dateFrom,
              endDate: filters.report.dateTo,
              shortcut: filters.report.floatingShortcutName,
            }}
            defaultValue={
              defaultReportFilters.dateFrom && defaultReportFilters.dateTo
                ? {
                    startDate: defaultReportFilters.dateFrom,
                    endDate: defaultReportFilters.dateTo,
                    shortcut: defaultReportFilters.floatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  dateFrom: newValue?.startDate,
                  dateTo: newValue?.endDate,
                  floatingShortcutName: newValue?.shortcut,
                },
              });
            }}
            showShortcuts
          />
          <LightSelectFilter
            config={{
              name: 'haiState',
              ui: {label: 'reports.filters.haiState'},
            }}
            options={haiReportsStateOptions}
            value={filters.report.haiState}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  haiState: newValue ?? defaultReportFilters.haiState,
                },
              });
            }}
            isClearable={false}
          />
          {standardType !== 'NO_TYPES' && (
            <LightSelectFilter
              config={{
                name: 'haiTypeCategory',
                ui: {label: 'reports.filters.haiTypeCategory'},
              }}
              options={haiTypeCategoryOptions}
              value={filters.report.typeCategory}
              onChange={(newValue) => {
                changeFilters({
                  report: {
                    ...filters.report,
                    typeCategory: newValue ?? defaultReportFilters.typeCategory,
                  },
                });
              }}
              isClearable={false}
            />
          )}
          <LightSelectFilter
            config={{
              name: 'haiMeasureType',
              ui: {label: 'reports.filters.haiMeasureType'},
            }}
            options={sortByMessageKey(haiMeasureTypeOptions ?? []).reverse()}
            value={filters.report.measureType}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  measureType: newValue ?? defaultReportFilters.measureType,
                },
              });
            }}
            isClearable={false}
          />
        </LightFilterPanel>
      </div>
      <HospitalReportTableAndCharts
        departmentTableAndChartLabel='reports.haiCountPerHospital'
        departmentColumnLabel='reports.haiCountPerHospital.departmentName'
        getReportLink={(data) => {
          const {departmentId} = data;
          const linkFilters: HospitalReportLinkFilters = {
            report: {
              dateFrom: filters.report.dateFrom,
              dateTo: filters.report.dateTo,
              haiState: filters.report.haiState,
              typeCategory: filters.report.typeCategory,
              measureType: filters.report.measureType,
            },
          };
          if (departmentId) linkFilters.report.organizationId = String(departmentId);
          if (!data.isTotalRow) return `/haiByDepartments?${asQueryString(linkFilters)}`;
          return undefined;
        }}
        muiStyles={muiStyles}
        storageIdPrefix={storageIdPrefix}
        filters={filters}
        changeFilters={changeFilters}
        hasFilters={hasFilters}
        handleResetFilters={() => changeFilters({report: defaultReportFilters})}
        hasSorting={hasSorting}
        handleResetSorting={() =>
          changeFilters({
            reportSorting: DEFAULT_REPORT_SORTING,
          })
        }
        formatters={formatters}
        isLoading={isLoading}
        reportData={reportData}
        config={config}
        reportName='HAI_MULTIPLE_DEPARTMENTS_REPORT'
      />
    </div>
  );
};
